import React, { useState, useEffect, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { Typography, Box, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { theme } from "../../../Theme";
import { getUrl } from "src/Utils/Common";
import FacebookCircularProgress from "src/Components/shared/shared/Loading";
import CustomSelectBox from "src/Components/shared/shared/CustomSelectBox";

function MarketGrowthContribution({
  selectedHierarchy: propSelectedHierarchy,
  setSelectedHierarchy: propSetSelectedHierarchy,
  onDataLoaded,
} = {}) {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(muiTheme.breakpoints.between("sm", "md"));
  const isMidRange = useMediaQuery("(min-width:900px) and (max-width:1100px)");
  
  const projectId = useSelector((state) => state.userProject.projectId);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState({});
  const [series, setSeries] = useState([]);
  
  const [hierarchyOptions, setHierarchyOptions] = useState([]);
  const [localSelectedHierarchy, setLocalSelectedHierarchy] = useState("brand");
  const selectedHierarchy =
    propSelectedHierarchy !== undefined ? propSelectedHierarchy : localSelectedHierarchy;
  const setSelectedHierarchy =
    propSetSelectedHierarchy !== undefined ? propSetSelectedHierarchy : setLocalSelectedHierarchy;
  const [loadingHierarchy, setLoadingHierarchy] = useState(true);

  const chartWrapperRef = useRef(null);

  useEffect(() => {
    function fetchHierarchyLevels() {
      if (!projectId) {
        console.warn("Project ID is not set in Redux state.");
        setError("Project ID not available");
        setLoadingHierarchy(false);
        return;
      }
      setLoadingHierarchy(true);
      setError(null);
      fetch(`${getUrl()}/market_overview/hierarchy`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-access-token": localStorage.getItem("userId") || "",
          "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
        },
        body: JSON.stringify({ project_id: projectId }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          return response.json();
        })
        .then((result) => {
          console.log("result from hierarchy", result);
          const hierarchyData =
            result?.["0"] ||
            result?.Product ||
            result?.product ||
            {};

          if (Object.keys(hierarchyData).length === 0) {
            throw new Error("No hierarchy data available");
          }
          
          const allowedHierarchyLevels = ["brand", "class", "manufacturer", "segment"];
          const rawHierarchyLevels = Object.keys(hierarchyData);
          const hierarchyLevels = rawHierarchyLevels.filter((key) =>
            allowedHierarchyLevels.includes(key)
          );
          const finalHierarchyOptions = [
            ...hierarchyLevels,
            "channel",
            "retailer"
          ];
          setHierarchyOptions(finalHierarchyOptions);
          if (propSetSelectedHierarchy === undefined) {
            setSelectedHierarchy((prev) => {
              if (!prev || !hierarchyLevels.includes(prev)) {
                return hierarchyLevels.includes("brand")
                  ? "brand"
                  : hierarchyLevels[0] || "";
              }
              return prev;
            });
          }
          setError(null);
        })
        .catch((error) => {
          console.error("Error fetching hierarchy levels:", error);
          setError(error.message || "Failed to fetch hierarchy levels");
        })
        .finally(() => {
          setLoadingHierarchy(false);
        });
    }

    fetchHierarchyLevels();
  }, [projectId]);

  useEffect(() => {
    fetchMarketGrowthData();
  }, [projectId, selectedHierarchy]);

  const fetchMarketGrowthData = async () => {
    if (!projectId) {
      console.warn("Project ID is not set in Redux state.");
      setError("Project ID not available");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const apiUrl = getUrl();
      console.log('Fetching data from:', `${apiUrl}/market_overview/market_growth_contribution_waterfall?project_id=${projectId}&filters={}&hierarchy=${selectedHierarchy}&time_period=P12M&target=5`);
      
      const response = await fetch(
        `${apiUrl}/market_overview/market_growth_contribution_waterfall?project_id=${projectId}&filters={}&hierarchy=${selectedHierarchy}&time_period=P12M&target=5`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-access-token": localStorage.getItem("userId") || "",
            "x-cors-api-key": "temp_8c8f91f122df0ef9daa49a6701f13f76",
          },
        }
      );
      
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      
      const hierarchyField = selectedHierarchy;
      
      if (!data[hierarchyField] || !data.value_sales_iya) {
        throw new Error(`Invalid data structure: missing ${hierarchyField} or value_sales_iya`);
      }
      
      console.log(`${hierarchyField} data:`, data[hierarchyField]);
      console.log('Value sales IYA:', data.value_sales_iya);
      
      setChartData({
        categories: data[hierarchyField],
        changes: data.value_sales_iya
      });
      const flatData = data[hierarchyField].map((name, i) => ({
        name,
        value_sales_iya: data.value_sales_iya[i]
      }));
      if (onDataLoaded) onDataLoaded(flatData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching market growth data:', error);
      console.error('Error details:', error.message);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setChartData(null);
    setError(null);
    setLoading(true);
    fetchMarketGrowthData();
  };

  const isLoading = loading || loadingHierarchy;

  const chartHeight = isMobile ? 280 : isTablet || isMidRange ? 320 : 350;
  useEffect(() => {
    if (chartData) {
      const { categories, changes } = chartData;

      const startValue = changes[0];
      const endValue = changes[changes.length - 1];
      console.log('Start value:', startValue, 'End value:', endValue);

      let cumulative = startValue;
      const cumulativeValues = [cumulative];
      
      for (let i = 1; i < changes.length - 1; i++) {
        cumulative += changes[i];
        cumulativeValues.push(cumulative);
      }

      const baseValues = [];
      const increaseValues = [];
      const decreaseValues = [];
      
      categories.forEach((cat, index) => {
        if (index === 0) {
          baseValues.push(0);
          increaseValues.push(0);
          decreaseValues.push(0);
        } else if (index === categories.length - 1) {
          baseValues.push(0);
          increaseValues.push(0);
          decreaseValues.push(0);
        } else {
          const prevValue = cumulativeValues[index - 1];
          let change = changes[index];
          if (Math.abs(change) < 0.001) change = 0;
          
          const isIncrease = change > 0;
          const baseForPoint = isIncrease ? prevValue : prevValue + change;
          baseValues.push(baseForPoint);
          
          if (isIncrease) {
            increaseValues.push(change);
            decreaseValues.push(0);
          } else {
            increaseValues.push(0);
            decreaseValues.push(Math.abs(change));
          }
        }
      });

      const totalData = categories.map((cat, index) => {
        if (index === 0) return startValue;
        if (index === categories.length - 1) return endValue;
        return 0;
      });
      const newSeries = [
        {
          name: "Base",
          data: baseValues,
        },
        {
          name: "Increase",
          data: increaseValues,
        },
        {
          name: "Decrease",
          data: decreaseValues,
        },
        {
          name: "Total",
          data: totalData,
        },
      ];

      const fontSize = isMobile ? "10px" : isTablet || isMidRange ? "11px" : "12px";
      const xAxisFontSize = isMobile ? "9px" : isTablet || isMidRange ? "10px" : "12px";
      const annotationFontSize = fontSize;
      const columnWidth = isMobile ? "60%" : isTablet ? "55%" : isMidRange ? "40%" : "50%";
      const tickAmount = isMobile ? 5 : isTablet || isMidRange ? 6 : 7;
      const dataLabelOffsetY = isMobile ? -15 : isTablet || isMidRange ? -18 : -20;

      const numCats = categories.length;
      const topValues = [startValue];
      let cum = startValue;
      for (let i = 1; i < changes.length - 1; i++) {
        cum += changes[i];
        topValues.push(cum);
      }
      topValues.push(changes[changes.length - 1]);
      const yMax = Math.max(...cumulativeValues, startValue) + 5;
      const yMin = 0;

      const newOptions = {
        chart: {
          type: "bar",
          height: chartHeight,
          width: "100%",
          stacked: true,
          toolbar: {
            show: false,
          },
          events: {
            rendered: function (chartContext) {
              const el = chartContext.el;
              el.querySelectorAll(".waterfall-connector").forEach((n) => n.remove());
              const svg = el.querySelector("svg");
              if (!svg) return;
              const g = chartContext.w.globals;
              const translateX = g.translateX || 0;
              const translateY = g.translateY || 0;
              const gridWidth = g.gridWidth || 0;
              const gridHeight = g.gridHeight || 0;
              if (!gridWidth || !gridHeight) return;
              const yAxis = chartContext.w.config.yaxis;
              const yAxisMin = Array.isArray(yAxis) ? (yAxis[0] && yAxis[0].min) : (yAxis && yAxis.min);
              const yAxisMax = Array.isArray(yAxis) ? (yAxis[0] && yAxis[0].max) : (yAxis && yAxis.max);
              const yMinVal = yAxisMin != null ? yAxisMin : yMin;
              const yMaxVal = yAxisMax != null ? yAxisMax : yMax;
              const dataToPixelY = (val) =>
                translateY + gridHeight - ((val - yMinVal) / (yMaxVal - yMinVal)) * gridHeight;
              const columnWidthStr = chartContext.w.config.plotOptions?.bar?.columnWidth ?? "50%";
              const barWidthRatio = parseFloat(String(columnWidthStr).replace("%", "")) / 100;
              const slotWidth = gridWidth / numCats;
              const barWidth = slotWidth * barWidthRatio;
              const totalColor = theme.palette.primary.charts || "#1976d2";
              const increaseColor = theme.palette.success.main || "#2e7d32";
              const decreaseColor = theme.palette.accent?.charts || theme.palette.error?.main || "#d32f2f";
              for (let i = 0; i < numCats - 1; i++) {
                const barIndex = i;
                const strokeColor = barIndex === 0
                  ? totalColor
                  : (changes[barIndex] > 0 ? increaseColor : decreaseColor);
                const connY = dataToPixelY(topValues[i]);
                const x1 = translateX + (i + 0.5) * slotWidth + barWidth / 2;
                const x2 = translateX + (i + 1 + 0.5) * slotWidth - barWidth / 2;
                const line = document.createElementNS("http://www.w3.org/2000/svg", "line");
                line.setAttribute("x1", x1);
                line.setAttribute("y1", connY);
                line.setAttribute("x2", x2);
                line.setAttribute("y2", connY);
                line.setAttribute("stroke", strokeColor);
                line.setAttribute("stroke-opacity", "1");
                line.setAttribute("stroke-width", "0.5");
                line.setAttribute("stroke-dasharray", "5 3");
                line.setAttribute("class", "waterfall-connector");
                svg.appendChild(line);
              }
            },
            updated: function (chartContext) {
              if (chartContext.w.config.chart.events && chartContext.w.config.chart.events.rendered) {
                chartContext.w.config.chart.events.rendered(chartContext);
              }
            },
          },
        },
        plotOptions: {
          bar: {
            columnWidth: columnWidth,
            borderRadius: 0,
            dataLabels: {
              position: "top",
            },
          },
        },
        dataLabels: {
          enabled: true,
          formatter: function (val, opts) {
            const seriesIndex = opts.seriesIndex;
            const dataPointIndex = opts.dataPointIndex;
            
            if (seriesIndex === 0) return "";
            
            if (seriesIndex === 1) {
              if (val && val > 0 && Math.abs(val) >= 0.01) return val.toFixed(1);
            }
            
            if (seriesIndex === 2) return "";
            
            if (seriesIndex === 3) {
              if (dataPointIndex === 0) return startValue.toFixed(1);
              if (dataPointIndex === categories.length - 1) return endValue.toFixed(1);
            }
            
            return "";
          },
          style: {
            fontSize: fontSize,
            fontWeight: 600,
            colors: ["#333"],
          },
          offsetY: dataLabelOffsetY,
          dropShadow: {
            enabled: false,
          },
        },
        annotations: {
          points: categories.map((cat, index) => {
            if (index === 0 || index === categories.length - 1) return null;
            let change = changes[index];
            if (Math.abs(change) < 0.001) change = 0;
            if (change < 0) {
              const barBottom = cumulativeValues[index];
              const yPosition = barBottom - 10;
              
              return {
                x: cat,
                y: yPosition,
                marker: {
                  size: 0,
                },
                label: {
                  borderColor: "transparent",
                  style: {
                    color: theme.palette.error.main,
                    fontSize: annotationFontSize,
                    fontWeight: 600,
                  },
                  text: `${change.toFixed(2)}`,
                  textAnchor: "middle",
                },
              };
            }
            if (change >= 0) {
              const barTop = cumulativeValues[index];
              const yPosition = barTop;
              
              return {
                x: cat,
                y: yPosition,
                marker: {
                  size: 0,
                },
                label: {
                  borderColor: "transparent",
                  style: {
                    color: change === 0 ? "#666" : theme.palette.success.main,
                    fontSize: annotationFontSize,
                    fontWeight: 600,
                  },
                  text: change === 0 ? "0" : `+${change.toFixed(2)}`,
                  textAnchor: "middle",
                },
              };
            }
            return null;
          }).filter(Boolean),
        },
        colors: [
          "transparent",
          theme.palette.success.main,
          theme.palette.accent.charts,
          theme.palette.primary.charts,
        ], 
        xaxis: {
          categories: categories,
          labels: {
            style: {
              fontSize: xAxisFontSize,
            },
            maxHeight: isMobile ? 60 : undefined,
            offsetY: isMobile ? 5 : 0,
            rotate: isMobile ? -45 : 0,
            rotateAlways: isMobile ? true : false,
            trim: false,
            hideOverlappingLabels: false,
            formatter: function (value) {
          if (!value || typeof value !== "string") return value ?? "";
          const words = value.split(" ");
          
          if (words.length <= 1) return [value];
          
          const lines = [];
          let i = 0;
          
          while (i < words.length) {
            if (i === 0 && words[i].length <= 3 && words.length > 1) {
              lines.push(words.slice(i, i + 2).join(" "));
              i += 2;
            } else {
              lines.push(words[i]);
              i += 1;
            }
          }
          
          return lines;
        },
          },
        },
        yaxis: {
          title: {
            text: "Market Value (%)",
            style: {
              fontSize: fontSize,
            },
          },
          min: 0,
          max: Math.max(...cumulativeValues, startValue) + 5,
          tickAmount: tickAmount,
          labels: {
            style: {
              fontSize: fontSize,
            },
            formatter: function(value) {
              return value.toFixed(0);
            },
          },
        },
        grid: {
          show: true,
          borderColor: "#e0e0e0",
          strokeDashArray: 0,
          position: "back",
          xaxis: {
            lines: {
              show: false,
            },
          },
          yaxis: {
            lines: {
              show: true,
            },
          },
        },
        legend: {
          show: true,
          position: "top",
          horizontalAlign: "center",
          fontSize: fontSize,
          markers: {
            width: isMobile ? 10 : isTablet || isMidRange ? 11 : 12,
            height: isMobile ? 10 : isTablet || isMidRange ? 11 : 12,
            radius: 0,
          },
          formatter: function (seriesName) {
            const legendMap = {
              Base: "",
              Increase: "Increase",
              Decrease: "Decrease",
              Total: "Total",
            };
            return legendMap[seriesName] || "";
          },
        },
        tooltip: {
          enabled: true,
          shared: true,
          intersect: false,
          followCursor: true,
          custom: function({ series, dataPointIndex, w }) {
            const categoryName = w.config.xaxis.categories[dataPointIndex] || `Item ${dataPointIndex + 1}`;
            const totalCategories = w.config.xaxis.categories.length;
            const isFirstColumn = dataPointIndex === 0;
            const isLastColumn = dataPointIndex === totalCategories - 1;
            
            const tooltipFontSize = isMobile ? "12px" : isTablet || isMidRange ? "13px" : "14px";
            let tooltipContent = `<div style="padding: ${isMobile ? '8px' : '10px'}; background: #fff; border-radius: 6px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); font-size: ${tooltipFontSize};">`;
            tooltipContent += `<div style="font-weight: bold; margin-bottom: 8px; color: theme.palette.primary.main;">${categoryName}</div>`;
            
            for (let i = 1; i < series.length; i++) { 
              const value = series[i][dataPointIndex];
              const isZeroChange = (!isFirstColumn && !isLastColumn && series[1][dataPointIndex] === 0 && series[2][dataPointIndex] === 0);
              const showAsZero = isZeroChange && i === 1;

              if ((value !== null && value !== undefined && value !== 0) || showAsZero) {
                const seriesNames = ["Base", "Increase", "Decrease", "Total"];
                let seriesName = showAsZero ? "Change" : seriesNames[i];              
                let displayValue = value;
                let valueColor = showAsZero ? "#666" : theme.palette.success.main;
                if (i === 2) {
                  displayValue = Math.abs(value);
                  valueColor = theme.palette.error.main;
                }
                
                if ((isFirstColumn && i === 3) || (isLastColumn && i === 3)) {
                  valueColor = theme.palette.primary.charts || "#333";
                }
                
                tooltipContent += `<div style="display: flex; justify-content: space-between; margin: 4px 0;">`;
                tooltipContent += `<span style="color: #666;">${seriesName}:</span>`;
                tooltipContent += `<span style="font-weight: bold; color: ${valueColor};">`;
                tooltipContent += `${i === 2 ? '-' : ''}${displayValue.toFixed(2)}%</span>`;
                tooltipContent += `</div>`;
              }
            }
            
            tooltipContent += `</div>`;
            return tooltipContent;
          },
        },
        fill: {
          opacity: 1,
        },
      };

      setSeries(newSeries);
      setOptions(newOptions);
    }
  }, [chartData, isMobile, isTablet, isMidRange]);

  return (
    <Box
      sx={{
        width: {
          xs: "100%",
          sm: "100%",
          md: isMidRange ? "700px" : "800px",
        },
        maxWidth: isMidRange ? "850px" : "900px",
        px: { xs: 1, sm: 2 },
        mx: "auto",
      }}
    >
      <Typography
        sx={{
          fontWeight: "bold",
          fontSize: { xs: "0.95rem", sm: "1.05rem", md: "1.15rem" },
          px: { xs: 0.5, sm: 0.75, md: 1 },
          color: theme.palette.primary.main,
          textAlign: "center",
          lineHeight: 1,
          mb: 2,
        }}
      >
        Market Growth Contribution (Waterfall)
      </Typography>

      <Box
        display="flex"
        justifyContent="center"
        mb={1}
        px={{ xs: 1, sm: 0 }}
      >
        <CustomSelectBox
          sx={{
            fontWeight: 500,
            fontSize: {
              xs: "14px",
              sm: "16px",
              md: "18px",
            },
            height: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            minHeight: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            maxHeight: {
              xs: 32,
              sm: 36,
              md: 40,
            },
            p: 0,
            mb: 2,
            display: "flex",
            alignItems: "center",
          }}
          value={hierarchyOptions.includes(selectedHierarchy) ? selectedHierarchy : ""}
          onChange={(event) => {
            setSelectedHierarchy(event.target.value);
          }}
          options={hierarchyOptions}
        />
      </Box>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <FacebookCircularProgress loading={true} hasData={true} />
        </Box>
      ) : error || !chartData || !chartData.categories || chartData.categories.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        >
          <FacebookCircularProgress
            loading={false}
            hasData={false}
            onRefresh={handleRefresh}
            message={
              error
                ? `Error loading data: ${error} - Click to retry`
                : "No data available - Click to reset chart"
            }
          />
        </Box>
      ) : (
      <Box
        ref={chartWrapperRef}
        sx={{
          position: "relative",
          width: "100%",
          overflowX: isMidRange ? "auto" : "visible",
          pb: isMidRange ? 1 : 0,
        }}
      >
        <Box sx={{ width: isMidRange ? "85%" : "100%", mx: "auto" }}>
          <ReactApexChart
            options={options}
            series={series}
            type="bar"
            height={chartHeight}
            width="100%"
          />
        </Box>
      </Box>
      )}
    </Box>
  );
}

export default MarketGrowthContribution;